<template>
  <AuthForm
    class="profile-page__form"
    :model="form"
    :rules="rules"
    layout="vertical"
    @finish="handleFinish"
  >
    <div class="profile-page__avatar">
      <a-avatar :size="128" shape="square" class="avatar">{{ avatar }}</a-avatar>
    </div>
    <a-form-item :label="t('ui.firstname-input-label')" name="first_name" :rules="rules.first_name">
      <a-input
        v-model:value="form.first_name"
        :placeholder="t('ui.firstname-input-placeholder')"
        size="large"
      />
    </a-form-item>

    <a-form-item :label="t('ui.lastname-input-label')" name="last_name" :rules="rules.last_name">
      <a-input
        v-model:value="form.last_name"
        :placeholder="t('ui.lastname-input-placeholder')"
        size="large"
      />
    </a-form-item>

    <a-form-item :label="t('ui.email-input-label')" name="email">
      <a-input
        v-model:value="form.email"
        placeholder="email@email.com"
        size="large"
        :disabled="true"
      />
    </a-form-item>

    <template #actions>
      <a-button
        class="profile-page__form-button full-width"
        type="primary"
        html-type="submit"
        :loading="loading"
        :disabled="hasError"
      >
        {{ t('ui.submit-button-label') }}
      </a-button>
    </template>
  </AuthForm>
</template>

<script setup lang="ts">
import { ref, reactive, watch } from 'vue'
import { AuthForm } from '@/ui/common/auth'
import type { Rule } from 'ant-design-vue/es/form'
import { storeToRefs } from 'pinia'

import { Form } from 'ant-design-vue'
import { useAppNotification } from '@/composables/use-app-notification'
import { useI18n } from 'vue-i18n'
import { UserInfo } from '@/api/modules/user/types'
import { useUserStore } from '@/store/user-store'
const { t } = useI18n()

const userStore = useUserStore()

const { loading, profile } = storeToRefs(userStore)

const hasError = ref<boolean>(true)

const { success } = useAppNotification()

const emit = defineEmits(['close'])

const form = reactive<UserInfo>({
  email: profile.value?.email || '',
  first_name: profile.value?.first_name || '',
  last_name: profile.value?.last_name || '',
})

const rules = computed<Record<string, Rule[]>>(() => ({
  // email: [{ required: true, type: 'email', message: t('rules.email-required-text') }],
  first_name: [{ required: true, message: t('rules.firstname-required-text') }],
  last_name: [{ required: true, message: t('rules.lastname-required-text') }],
}))

const avatar = computed(() =>
  profile.value?.first_name && profile.value.last_name
    ? profile.value?.first_name[0].toLocaleUpperCase() +
      profile.value?.last_name[0].toLocaleUpperCase()
    : 'U',
)

const { validate } = Form.useForm(form, rules)

const handleFinish = async (formData: any) => {
  try {
    await userStore.updateUserInfo(formData)
    success(t('page.profile-update-success-title'), t('page.profile-update-success-text'))

    emit('close')
  } catch (e) {
    console.log(e)
  }
}

watch(
  () => form,
  async () => {
    try {
      await validate()
      hasError.value = false
    } catch {
      hasError.value = true
    }
  },
  { deep: true },
)

onMounted(async () => {
  await userStore.userInfo()
})
</script>

<style lang="scss" scoped>
.profile-page {
  &__avatar {
    display: flex;
    height: 128px;
    width: 100%;
    align-items: center;
    justify-content: center;
    & .avatar {
      background-color: var(--brand_10);
      color: var(--brand_40);
      border-radius: 10px;
    }
  }

  &__form {
    margin-top: 36px;
  }

  &__form-button {
    margin-bottom: 8px;
  }
}
</style>
